import type { ImageStore } from 'visualizations/image-store'
import type { SettingsInterface, NodeInterface } from './types'
import { Settings } from './settings'
import { Node } from './node'
import type { Simulation } from 'd3-force'
import { forceSimulation, forceX, forceY, forceCollide } from 'd3-force'
import { zoom, zoomIdentity } from 'd3-zoom'
import { select } from 'd3-selection'
import * as PIXI from 'pixi.js'

export class SegmentMap {
  private readonly element: HTMLElement
  private readonly settings: Settings
  private readonly imageStore: ImageStore
  private readonly application: PIXI.Application
  private readonly container: PIXI.Container
  private readonly nodes: Node[] = []
  private readonly simulation: Simulation
  private readonly zoomBehavior: any
  private readonly groupNameLabels: PIXI.Text[] = []
  private readonly groupCountLabels: PIXI.Text[] = []
  private verticalLinesContainer: PIXI.Container
  private animationLock: boolean = false
  private eventListeners: { [key: string]: Array<(...args: any[]) => void> } = {}

  constructor (element: HTMLElement, settings: SettingsInterface, nodes: NodeInterface[], imageStore: ImageStore) {
    PIXI.Graphics.curves.adaptive = false

    this.element = element
    this.settings = new Settings(settings)
    this.imageStore = imageStore
    this.application = this.initializeApplication()
    this.container = new PIXI.Container()
    this.nodes = this.initializeNodes(nodes)
    this.simulation = this.initializeSimulation()
    this.zoomBehavior = this.initializeZoomBehavior()

    this.draw()

    select(this.application.view).call(this.zoomBehavior)
    select(this.application.view).call(this.zoomBehavior.transform, zoomIdentity)
    this.application.stage.addChild(this.container)
    this.element.appendChild(this.application.view)
  }

  private initializeApplication (): void {
    return new PIXI.Application({
      resizeTo: this.element,
      resolution: window.devicePixelRatio ?? 1,
      autoDensity: true,
      antialias: true,
      eventMode: 'passive',
      background: 0xeeeeee
    })
  }

  private initializeNodes (nodes: NodeInterface[]): Node[] {
    return nodes.map(node => ({
      ...node,
      x: this.settings.initialXPosition(node.groupId, this.width, this.xOffset),
      y: this.settings.initialYPosition(node.groupId, this.height)
    })).map(node => new Node(node, this))
  }

  private initializeSimulation (): Simulation {
    return forceSimulation(this.nodes)
      .force('x', forceX(d => d.x).strength(1))
      .force('y', forceY(d => d.y).strength(0.1))
      .force('collide', forceCollide().radius(d => d.collisionRadius).strength(1))
      .alphaDecay(0.01)
      .alpha(0.3)
      .on('tick', () => this.tick())
  }

  private initializeZoomBehavior (): any {
    return zoom()
      .scaleExtent([1, 10])
      .on('zoom', (event) => {
        if (this.animationLock) return
        this.applyTransform(event.transform)
      })
  }

  private draw (): void {
    this.drawBackground()
    this.nodes.forEach(node => {
      node.initializeGraphics(this.container, () => this.click(node), () => this.hover(node), () => this.unhover(node))
    })
    this.drawGroupLabels()
    this.drawVerticalLines()
  }

  private tick (): void {
    this.nodes.forEach((node: Node) => {
      if (node.graphics === undefined) return
      node.graphics.x = node.x
      node.graphics.y = node.y
    })
  }

  private drawBackground (): void {
    const background = new PIXI.Graphics()
    background.beginFill(0xeeeeee)
    background.eventMode = 'static'
    background.drawRect(0, 0, this.application.view.width, this.application.view.height)
    background.endFill()
    this.application.stage.addChildAt(background, 0)
    background.on('pointerdown', () => this.unclick())
  }

  private applyTransform (transform: any): void {
    this.container.scale.set(transform.k)
    this.container.position.set(transform.x, transform.y)
    this.transformAssets(transform.k)
  }

  public on (event: string, listener: (...args: any[]) => void): void {
    if (this.eventListeners[event] === undefined) {
      this.eventListeners[event] = []
    }
    this.eventListeners[event].push(listener)
  }

  private emit (event: string, ...args: any[]): void {
    if (this.eventListeners[event] !== undefined) {
      this.eventListeners[event].forEach(listener => listener(...args))
    }
  }

  private click (node: Node): void {
    this.highlight(node)
    this.center(node)
    this.emit('click', node.id)
  }

  private unclick (): void {
    this.unhighlight()
    this.resetGroupCounts()
    this.emit('unclick')
  }

  private hover (node: Node): void {
    this.element.classList.add('cursor-pointer')
    this.emit('hover', node.id)
  }

  private unhover (node: Node): void {
    this.element.classList.remove('cursor-pointer')
    this.emit('unhover', node.id)
  }

  private highlight (node: Node): void {
    this.nodes.forEach(n => {
      if (n.id === node.id) {
        n.graphics.alpha = 1
      } else {
        n.graphics.alpha = 0.2
      }
    })
  }

  public highlightByColorId (colorId: string): void {
    this.nodes.forEach(n => {
      if (n.colors.map(c => c.id).includes(colorId)) {
        n.graphics.alpha = 1
      } else {
        n.graphics.alpha = 0.1
      }
    })
  }

  public updateGroupCounts (highlightedColor: any): void {
    this.groupNameLabels.forEach(label => {
      const count = highlightedColor.counts.find(c => c.id === label.groupId).count
      label.children[3].text = count.toString()
    })
  }

  public resetGroupCounts (): void {
    this.groupNameLabels.forEach(label => {
      const count = this.settings.groups.find(g => g.id === label.groupId).count
      label.children[3].text = count.toString()
    })
  }

  private unhighlight (): void {
    this.nodes.forEach(n => {
      n.graphics.alpha = 1
    })
  }

  private center (node: Node): void {
    const targetX = this.width / 2 - node.x * this.container.scale.x
    const targetY = this.height / 2 - node.y * this.container.scale.y
    const targetScale = this.container.scale.x

    const transform = zoomIdentity.translate(targetX, targetY).scale(targetScale)

    const ticker = new PIXI.Ticker()
    this.animationLock = true

    const maxDuration = 500
    const startTime = performance.now()

    ticker.add((delta) => {
      this.container.position.x += (targetX - this.container.position.x) * 0.1 * delta
      this.container.position.y += (targetY - this.container.position.y) * 0.1 * delta
      this.container.scale.x += (targetScale - this.container.scale.x) * 0.1 * delta
      this.container.scale.y += (targetScale - this.container.scale.y) * 0.1 * delta

      this.transformAssets(this.container.scale.x)

      const currentTime = performance.now()
      const duration = currentTime - startTime
      const distanceReached = Math.abs(this.container.position.x - targetX) < 0.01 && Math.abs(this.container.position.y - targetY) < 0.01
      const timeElapsed = duration > maxDuration

      if (distanceReached || timeElapsed) {
        ticker.stop()
        select(this.application.view).call(this.zoomBehavior.transform, transform)
        this.animationLock = false
      }
    })
    ticker.start()
  }

  private transformAssets (scale: number): void {
    this.allLabels.forEach(label => {
      if (scale < 0.8) {
        label.scale.set(1)
        label.y = 0
      } else {
        label.scale.set(1 / scale)
        label.y = 0 - this.container.position.y / scale + (label.height * 2)
      }
    })
    this.verticalLinesContainer.scale.set(scale)
    this.verticalLinesContainer.position.set(this.container.position.x, this.container.position.y)

    if (scale > 3) {
      this.verticalLinesContainer.alpha = 0
    } else {
      this.verticalLinesContainer.alpha = (1 / scale ** 2)
    }

    this.verticalLinesContainer.children.forEach((line, index) => {
      line.clear()
      line.lineStyle(2, 0xffffff, 1)
      line.moveTo((index + 1) * this.groupWidth, -this.container.position.y / scale)
      line.lineTo((index + 1) * this.groupWidth, (-this.container.position.y / scale) + this.height / scale)
    })
  }

  private drawGroupLabels (): void {
    this.settings.groups.forEach((group, index) => {
      const container = new PIXI.Container()

      const nameText = new PIXI.Text(group.name, {
        fontFamily: 'Inter',
        fontSize: 16,
        fill: 0x6f697d,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: this.groupWidth
      })

      const countText = new PIXI.Text(group.count.toString(), {
        fontFamily: 'Inter',
        fontSize: 14,
        fontWeight: '600',
        fill: 0x8c869e,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: this.groupWidth
      })

      const xPosition = (this.xOffset * (index * 2 + 1))
      const yPosition = 0

      const xPadding = 10
      const yPadding = 5
      const smallXPadding = 5
      const smallYPadding = 2
      const xMargin = 1
      const rounding = 30
      const navigationOffset = 45

      const labelWidth = xPadding + nameText.width + xMargin + smallXPadding + countText.width + smallXPadding + xPadding
      const labelHeight = yPadding + nameText.height + yPadding

      const labelXPosition = -labelWidth / 2
      const labelYPosition = -labelHeight + navigationOffset

      const nameBackground = new PIXI.Graphics()
      nameBackground.beginFill(0xffffff)
      nameBackground.drawRoundedRect(labelXPosition, labelYPosition, labelWidth, labelHeight, rounding)
      nameBackground.endFill()

      const countBackgroundXPosition = labelXPosition + labelWidth - xPadding - countText.width - smallXPadding
      const countBackgroundYPosition = labelYPosition + labelHeight - (labelHeight / 2) - (countText.height / 2) - smallYPadding
      const countBackgroundWidth = countText.width + smallXPadding * 2
      const countBackgroundHeight = countText.height + smallYPadding * 2

      const countBackground = new PIXI.Graphics()
      countBackground.beginFill(0xeeedf1)
      countBackground.drawRoundedRect(countBackgroundXPosition, countBackgroundYPosition, countBackgroundWidth, countBackgroundHeight, rounding)
      countBackground.endFill()

      container.x = xPosition
      container.y = yPosition

      nameText.anchor.set(0.5, 1.0)
      countText.anchor.set(0.5, 1.0)

      nameText.x = labelXPosition + xPadding + nameText.width / 2
      nameText.y = labelYPosition + yPadding + nameText.height

      countText.x = countBackgroundXPosition + countBackgroundWidth / 2
      countText.y = countBackgroundYPosition + countBackgroundHeight - smallYPadding

      container.addChild(nameBackground)
      container.addChild(nameText)
      container.addChild(countBackground)
      container.addChild(countText)

      container.groupId = group.id

      this.container.addChild(container)
      this.groupNameLabels.push(container)
    })
  }

  private drawVerticalLines (): void {
    const lineContainer = new PIXI.Container()

    this.settings.groups.forEach((group, index) => {
      if (index < this.settings.groups.length - 1) {
        const line = new PIXI.Graphics()
        line.lineStyle(2, 0xffffff, 1)
        line.moveTo((index + 1) * this.groupWidth, 0)
        line.lineTo((index + 1) * this.groupWidth, this.height)
        lineContainer.addChild(line)
      }
    })

    this.application.stage.addChild(lineContainer)
    this.verticalLinesContainer = lineContainer
  }

  public loadImages (): void {
    this.nodes.forEach(node => {
      node.loadImage()
    })
  }

  private get width (): number {
    return this.application.view.width / (window.devicePixelRatio ?? 1)
  }

  private get height (): number {
    return this.application.view.height / (window.devicePixelRatio ?? 1)
  }

  private get xOffset (): number {
    return (this.width / this.settings.groups.length) / 2
  }

  private get allLabels (): PIXI.Text[] {
    return this.groupNameLabels.concat(this.groupCountLabels)
  }

  private get groupWidth (): number {
    return this.width / this.settings.groups.length
  }
}
